import { Component, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ControlConfig } from '../../../models/api/reports/report';
import { ControlDependencyService } from '../../../services/reports/control-dependency.service';
import { RangeComponent } from '../../core/controls/range.component';
import { SelectComponent } from '../../core/controls/select.component';
import { FieldContainerComponent } from '../../core/fields/field-container.component';
import { InputFieldComponent } from '../../core/fields/input-field.component';
import { RangeFieldComponent } from '../../core/fields/range-field.component';
import { SelectFieldComponent } from '../../core/fields/select-field.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { BaseCriteriaComponent } from './base-criteria.component';
import { SharedControlConfigs } from './shared-control-configs';

interface DocumentCriteriaFormControls {
  payeeName?: FormControl<string | null>;
  voidStatus?: FormControl<string | null>;
  clearedStatus?: FormControl<string | null>;
  clearedFrom?: FormControl<Date | null>;
  clearedTo?: FormControl<Date | null>;
  escheatedStatus?: FormControl<string | null>;
  escheatedFrom?: FormControl<Date | null>;
  escheatedTo?: FormControl<Date | null>;
  allocationAmountFrom?: FormControl<number | null>;
  allocationAmountTo?: FormControl<number | null>;
  paymentAmountFrom?: FormControl<number | null>;
  paymentAmountTo?: FormControl<number | null>;
}

@Component({
  selector: 'app-document-criteria',
  imports: [
    InputFieldComponent,
    RangeFieldComponent,
    SelectFieldComponent,
    FieldContainerComponent,
    SelectComponent,
    RangeComponent,
    PageHeaderComponent,
  ],
  template: `
    <app-page-header label="Document criteria" headerSize="h3" />
    @if (showPayeeNameInDocument()) {
      <app-input-field
        [control]="fg.controls.payeeName"
        [label]="getLabel('PAYEE_NAME')"
        valueClasses="tw-max-w-[500px]"
      />
    }
    @if (showVoidStatus()) {
      <app-select-field
        [label]="getLabel('VOID_STATUS')"
        optionLabel="name"
        optionValue="code"
        [virtualScroll]="false"
        [showHeader]="false"
        [filter]="false"
        [multiple]="false"
        [showClear]="false"
        [options]="voidStatusOptions()"
        [control]="fg.controls.voidStatus"
        valueClasses="tw-max-w-[400px]"
      />
    }
    @if (showClearedStatus()) {
      <app-field-container
        [label]="getLabel('CLEARED_STATUS')"
        [control]="fg.controls.clearedStatus"
      >
        <ng-template #value>
          <div class="tw-flex tw-items-center tw-gap-4">
            <app-select
              [options]="clearedStatusOptions()"
              optionLabel="name"
              optionValue="code"
              [virtualScroll]="false"
              [showHeader]="false"
              [filter]="false"
              [multiple]="false"
              [showClear]="false"
              [control]="fg.controls.clearedStatus"
            />
            @if (showClearedBetween()) {
              <app-range
                [control]="fg.controls.clearedFrom"
                [toControl]="fg.controls.clearedTo"
                [compact]="true"
                type="Date"
                fromLabel="Cleared From"
                toLabel="Cleared To"
                [requireBoth]="true"
                [maxSpanDays]="null"
              />
            }
          </div>
        </ng-template>
      </app-field-container>
    }
    @if (showEscheatedStatus()) {
      <app-field-container
        [label]="getLabel('ESCHEATED_STATUS')"
        [control]="fg.controls.escheatedStatus"
      >
        <ng-template #value>
          <div class="tw-flex tw-items-center tw-gap-4">
            <app-select
              [options]="escheatedStatusOptions()"
              optionLabel="name"
              optionValue="code"
              [virtualScroll]="false"
              [showHeader]="false"
              [filter]="false"
              [multiple]="false"
              [showClear]="false"
              [control]="fg.controls.escheatedStatus"
            />
            @if (showEscheatedBetween()) {
              <app-range
                [control]="fg.controls.escheatedFrom"
                [toControl]="fg.controls.escheatedTo"
                [compact]="true"
                type="Date"
                fromLabel="Escheated From"
                toLabel="Escheated To"
                [requireBoth]="true"
                [maxSpanDays]="null"
              />
            }
          </div>
        </ng-template>
      </app-field-container>
    }
    @if (showAllocationAmountRange()) {
      <app-range-field
        label="Allocation amount"
        valueClasses="tw-max-w-[300px]"
        [control]="fg.controls.allocationAmountFrom"
        [toControl]="fg.controls.allocationAmountTo"
        [compact]="true"
        type="Currency"
        [requirePair]="true"
      />
    }
    @if (showPaymentAmountRange()) {
      <app-range-field
        label="Payment amount"
        valueClasses="tw-max-w-[300px]"
        [control]="fg.controls.paymentAmountFrom"
        [toControl]="fg.controls.paymentAmountTo"
        [compact]="true"
        type="Currency"
        [requirePair]="true"
      />
    }
  `,
})
export class DocumentCriteriaComponent extends BaseCriteriaComponent {
  private controlDependency = inject(ControlDependencyService);

  showPayeeNameInDocument = signal(false);
  showVoidStatus = signal(false);
  showClearedStatus = signal(false);
  showClearedDateRange = signal(false);
  showEscheatedStatus = signal(false);
  showEscheatedDateRange = signal(false);
  showAllocationAmountRange = signal(false);
  showPaymentAmountRange = signal(false);

  showClearedBetween = computed(() => {
    return (
      this.showClearedDateRange() &&
      this.controlDependency.isVisible('PAYMENT_CLEARED_FROM')()
    );
  });

  showEscheatedBetween = computed(() => {
    return (
      this.showEscheatedDateRange() &&
      this.controlDependency.isVisible('PAYMENT_ESCHEATED_FROM')()
    );
  });

  // Computed signals for template options
  voidStatusOptions = computed(() =>
    this.controlConfig.getControlOptions('VOID_STATUS'),
  );
  clearedStatusOptions = computed(() =>
    this.controlConfig.getControlOptions('CLEARED_STATUS'),
  );
  escheatedStatusOptions = computed(() =>
    this.controlConfig.getControlOptions('ESCHEATED_STATUS'),
  );

  fg = new FormGroup<DocumentCriteriaFormControls>({
    // Controls are created dynamically by control-configuration.service
  });

  protected readonly regionName = 'Document Criteria';

  protected CONTROL_CONFIGS: ControlConfig[] = [
    SharedControlConfigs.createPayeeNameConfig(this.showPayeeNameInDocument),
    {
      erfNames: 'VOID_STATUS',
      formControlName: 'voidStatus',
      visibility: this.showVoidStatus,
      defaultToFirstOption: true,
    },
    {
      erfNames: 'CLEARED_STATUS',
      formControlName: 'clearedStatus',
      visibility: this.showClearedStatus,
      defaultToFirstOption: true,
    },
    {
      erfNames: 'PAYMENT_CLEARED_FROM',
      formControlName: 'clearedFrom',
      visibility: this.showClearedDateRange,
      type: 'date',
    },
    {
      erfNames: 'PAYMENT_CLEARED_THROUGH',
      formControlName: 'clearedTo',
      visibility: this.showClearedDateRange,
      type: 'date',
    },
    {
      erfNames: 'ESCHEATED_STATUS',
      formControlName: 'escheatedStatus',
      visibility: this.showEscheatedStatus,
      defaultToFirstOption: true,
    },
    {
      erfNames: 'PAYMENT_ESCHEATED_FROM',
      formControlName: 'escheatedFrom',
      visibility: this.showEscheatedDateRange,
      type: 'date',
    },
    {
      erfNames: 'PAYMENT_ESCHEATED_THROUGH',
      formControlName: 'escheatedTo',
      visibility: this.showEscheatedDateRange,
      type: 'date',
    },
    {
      erfNames: 'ALLOCATION_AMOUNT_FROM',
      formControlName: 'allocationAmountFrom',
      visibility: this.showAllocationAmountRange,
      type: 'currency',
    },
    {
      erfNames: 'ALLOCATION_AMOUNT_THROUGH',
      formControlName: 'allocationAmountTo',
      visibility: this.showAllocationAmountRange,
      type: 'currency',
    },
    {
      erfNames: 'PAYMENT_AMOUNT_FROM',
      formControlName: 'paymentAmountFrom',
      visibility: this.showPaymentAmountRange,
      type: 'currency',
    },
    {
      erfNames: 'PAYMENT_AMOUNT_THROUGH',
      formControlName: 'paymentAmountTo',
      visibility: this.showPaymentAmountRange,
      type: 'currency',
    },
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.controlDependency.setupDependencyWatchers(this.fg, [
      'PAYMENT_CLEARED_FROM',
      'PAYMENT_CLEARED_THROUGH',
      'PAYMENT_ESCHEATED_FROM',
      'PAYMENT_ESCHEATED_THROUGH',
    ]);
  }

  protected getLabel(erfName: string): string | undefined {
    return this.controlConfig.getControlLabel(erfName);
  }
}
